/**
 * The four button treatments this app already had, named (D1's `ui/` primitives).
 *
 * Nothing here is new design: `primary` is the action bar's *do it*, `quiet` is the journal
 * toggle, `bare` is every *close*, and `danger` is the one the peripheral surfaces added — deleting a story is
 * the first irreversible thing a player can do, and it should not look like *cancel*.
 *
 * Scope note, so the inconsistency is a decision rather than a leftover: **The game UI's
 * components are not retrofitted onto this.** `ActionBar`, `Journal` and `CharacterPanel`
 * keep their inline classes. Rewriting a working, tested surface to save a string is the
 * churn D6 warns about, and the mobile pass is already going to be inside all three.
 */
import type { ComponentPropsWithRef } from 'react';

export type ButtonVariant = 'primary' | 'quiet' | 'bare' | 'danger';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border-accent text-accent hover:bg-accent enabled:hover:text-bg border px-4 py-2 transition-colors disabled:opacity-40',
  quiet:
    'border-border text-fg-muted hover:border-accent hover:text-accent border px-3 py-1.5 transition-colors disabled:opacity-40 disabled:hover:border-[color:var(--color-border)] disabled:hover:text-[color:var(--color-fg-muted)]',
  bare: 'text-fg-faint hover:text-fg px-2 py-1.5 transition-colors disabled:opacity-40',
  danger:
    'border-[color:var(--color-ramp-danger)] text-[color:var(--color-ramp-danger)] hover:bg-[color:var(--color-ramp-danger)] enabled:hover:text-bg border px-4 py-2 transition-colors disabled:opacity-40',
};

// `ComponentPropsWithRef` rather than `ButtonHTMLAttributes`: React 19 passes `ref` as an
// ordinary prop, so the spread below forwards it and callers that need the node — the delete
// dialog focuses its cancel button — need no `forwardRef` wrapper.
interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  /** `meta` is chrome, `action` is something the player is meant to press. */
  size?: 'meta' | 'action';
}

export function Button({
  variant = 'quiet',
  size = 'meta',
  className = '',
  type = 'button',
  style,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`font-mono ${VARIANTS[variant]} ${className}`}
      style={{ fontSize: size === 'meta' ? 'var(--text-meta)' : 'var(--text-action)', ...style }}
      {...rest}
    />
  );
}
